import { Controller } from "@hotwired/stimulus"

// Shared progressive-enhancement uploader. Server-side multipart forms remain
// functional without JavaScript; with JavaScript, validation/errors stay next
// to the selected file so a failed request does not discard the form state.
export default class extends Controller {
  static targets = ["input", "error", "status", "submit", "refresh"]
  static values = { refresh: Boolean, gallery: Boolean }

  async submit(event) {
    event.preventDefault()
    if (this.busy || this.persistedUploadId || this.refreshRequired) return

    const files = Array.from(this.inputTarget?.files || [])
    if (files.length === 0) {
      this.showError("Seleciona pelo menos um ficheiro para enviar.")
      return
    }

    this.clearError()
    this.hideStatus()
    this.setBusy(true)
    let outcomeKnown = false

    try {
      const response = await fetch(this.element.action, {
        method: (this.element.method || "post").toUpperCase(),
        headers: this.requestHeaders(),
        body: new FormData(this.element),
      })
      const body = await response.json().catch(() => null)

      if (!body || typeof body !== "object") {
        throw new Error("Não recebemos confirmação do envio. Verifica o estado antes de tentar novamente.")
      }

      if (body.refresh_required === true) {
        this.refreshRequired = true
        if (this.hasRefreshTarget) this.refreshTarget.hidden = false
      }

      if (!response.ok || body.success !== true) {
        outcomeKnown = body.success === false
        const errors = Array.isArray(body.errors) ? body.errors.join(" ") : body.error
        throw new Error(errors || "Não foi possível enviar o ficheiro. Tenta novamente.")
      }

      if (this.galleryValue) {
        if (!Number.isSafeInteger(body.id) || body.id <= 0) {
          throw new Error("Não recebemos a referência do envio. Consulta a galeria antes de enviar novamente.")
        }
        this.persistedUploadId = body.id
      }
      outcomeKnown = true

      this.showStatus(body.message || "Ficheiro enviado com sucesso.")
      this.element.dispatchEvent(new CustomEvent("file-upload:success", {
        bubbles: true,
        // Count is display-only; the concierge verifies the persisted upload ID.
        detail: this.galleryValue ? { upload_id: this.persistedUploadId, file_count: body.file_count } : body,
      }))
      if (this.refreshValue) {
        this.refreshRequired = true
        this.refresh()
      }
    } catch (error) {
      if (!outcomeKnown && (this.galleryValue || this.refreshValue)) {
        this.refreshRequired = true
        if (this.hasRefreshTarget) this.refreshTarget.hidden = false
      }
      this.showError(error?.message || "Não foi possível enviar o ficheiro. Tenta novamente.")
    } finally {
      this.setBusy(false)
    }
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
    this.inputTarget?.removeAttribute("aria-invalid")
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
    this.inputTarget?.setAttribute("aria-invalid", "true")
    this.inputTarget?.focus()
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.hidden = false
  }

  hideStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = ""
    this.statusTarget.hidden = true
  }

  setBusy(busy) {
    this.busy = busy
    this.element.setAttribute("aria-busy", busy ? "true" : "false")
    if (this.hasSubmitTarget) this.submitTarget.disabled = Boolean(busy || this.persistedUploadId || this.refreshRequired)
  }

  refresh() {
    window.location.reload()
  }

  requestHeaders() {
    const headers = { Accept: "application/json" }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) headers["X-CSRF-Token"] = token
    return headers
  }
}
